// src/components/analysis/detail/InvestorSummaryDetail.js
import React, { useState } from 'react'; // Add useState
import { useParams } from 'react-router-dom'; // Add useParams to get taskId
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Rating,
  Avatar,
  TextField, // Added
  Button, // Added
  CircularProgress, // Added
  Snackbar, // Added
  Alert as MuiAlert, // Added
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import RecommendIcon from '@mui/icons-material/Recommend';
import ErrorIcon from '@mui/icons-material/Error';
import ApiService from '../../../services/api'; // Added - Adjust path if necessary

// Added SnackbarAlert Component
const SnackbarAlert = React.forwardRef(function SnackbarAlert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const InvestorSummaryDetail = ({ data }) => {
  console.log("Investor Summary Data:", data);

  // Added State and Functions
  const { taskId } = useParams(); // Get taskId from URL
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' or 'error'

  const handleSendEmail = async () => {
    if (!recipientEmail || !/\S+@\S+\.\S+/.test(recipientEmail)) {
      setSnackbarMessage('Please enter a valid email address.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsSending(true);
    setSnackbarOpen(false); // Close previous snackbar if open

    try {
      // Use the taskId obtained from useParams
      const response = await ApiService.sendSummaryEmail(taskId, recipientEmail);
      setSnackbarMessage(response.message || 'Email sending process initiated successfully!');
      setSnackbarSeverity('success');
      setRecipientEmail(''); // Clear input on success
    } catch (err) {
      console.error('Failed to send email:', err);
      const errorMessage = err.detail || err.message || 'Failed to send email. Please check SMTP settings in the backend.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setIsSending(false);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Extrahiere das summary-Objekt aus den Daten
  const extractSummaryData = () => {
    if (!data) return null;
    
    // Wenn data bereits ein Objekt mit einem summary-Feld ist
    if (typeof data === 'object' && data.summary) {
      return data.summary;
    }
    
    // Wenn data ein String ist, versuche es zu parsen
    if (typeof data === 'string') {
      try {
        const parsedData = JSON.parse(data);
        if (parsedData.summary) {
          return parsedData.summary;
        }
        return parsedData; // Falls summary nicht existiert, verwende das ganze Objekt
      } catch (e) {
        // Versuche nach Markdown-Codeblöcken zu suchen
        const match = data.match(/```json\n([\s\S]*?)\n```/);
        if (match && match[1]) {
          try {
            const parsedJson = JSON.parse(match[1]);
            if (parsedJson.summary) {
              return parsedJson.summary;
            }
            return parsedJson;
          } catch (e2) {
            console.error("Failed to parse embedded JSON:", e2);
            return null;
          }
        }
        return null;
      }
    }
    
    return null;
  };

  const summary = extractSummaryData();
  console.log("Extracted Summary:", summary);
  
  // Display error if parsing failed
  if (!summary) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={2} sx={{ p: 3, bgcolor: '#fff9f9', borderLeft: '4px solid #f44336' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorIcon color="error" sx={{ mr: 2 }} />
            <Typography variant="h6">Fehler beim Parsen der Investor Summary Daten</Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {typeof data === 'string' ? data : 
             JSON.stringify(data, null, 2)}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section with Company Name and One-liner */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderLeft: '4px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <BusinessIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {summary.company_name || 'Unternehmen'}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {summary.one_liner || 'Keine Kurzbeschreibung verfügbar'}
            </Typography>
          </Box>
        </Box>
        
        {summary.investment_score && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 1 }}>
              Investment Score:
            </Typography>
            <Rating 
              value={parseFloat(summary.investment_score)} 
              precision={0.1} 
              readOnly
              icon={<StarIcon fontSize="inherit" color="primary" />}
              emptyIcon={<StarIcon fontSize="inherit" style={{ opacity: 0.55 }} />}
            />
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ ml: 1 }}>
              {summary.investment_score}/5
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Email Sending Section */}
      <Paper elevation={3} sx={{ p: 3, mt: 4, mb: 4 }}> {/* Added mt: 4 for spacing */}
         <Typography variant="h6" gutterBottom>
           Send Investor Summary via Email
         </Typography>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
           <TextField
             fullWidth
             label="Recipient Email Address"
             variant="outlined"
             type="email"
             value={recipientEmail}
             onChange={(e) => setRecipientEmail(e.target.value)}
             disabled={isSending}
             size="small"
           />
           <Button
             variant="contained"
             onClick={handleSendEmail}
             disabled={isSending || !recipientEmail}
             startIcon={isSending ? <CircularProgress size={20} color="inherit" /> : null}
           >
             {isSending ? 'Sending...' : 'Send Email'}
           </Button>
         </Box>
       </Paper>
      
      {/* Key Metrics Section */}
      {summary.key_metrics && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
            Key Metrics
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {summary.key_metrics.market_size && (
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Market Size
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {summary.key_metrics.market_size}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {summary.key_metrics.current_revenue && (
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Revenue
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {summary.key_metrics.current_revenue}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {summary.key_metrics.growth_rate && (
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Growth Rate
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {summary.key_metrics.growth_rate}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {summary.key_metrics.funding_ask && (
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Funding Ask
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {summary.key_metrics.funding_ask}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
      
      {/* Highlights and Concerns Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Highlights */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%', borderLeft: '4px solid #4caf50' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ mr: 1, color: '#4caf50' }} />
              Highlights
            </Typography>
            {summary.highlights && summary.highlights.length > 0 ? (
              <List dense>
                {summary.highlights.map((highlight, index) => (
                  <ListItem key={index} alignItems="flex-start" sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    </ListItemIcon>
                    <ListItemText primary={highlight} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Keine Highlights verfügbar
              </Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Concerns */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%', borderLeft: '4px solid #ff9800' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon sx={{ mr: 1, color: '#ff9800' }} />
              Concerns
            </Typography>
            {summary.concerns && summary.concerns.length > 0 ? (
              <List dense>
                {summary.concerns.map((concern, index) => (
                  <ListItem key={index} alignItems="flex-start" sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <WarningIcon sx={{ color: '#ff9800' }} />
                    </ListItemIcon>
                    <ListItemText primary={concern} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Keine Bedenken verfügbar
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Detailed Assessments */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Detailed Assessments
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {/* Team Assessment */}
          {summary.team_assessment && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PeopleIcon color="primary" sx={{ mr: 1 }} />
                    Team Assessment
                  </Typography>
                  <Typography variant="body2">
                    {summary.team_assessment}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Product Assessment */}
          {summary.product_assessment && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BuildIcon color="primary" sx={{ mr: 1 }} />
                    Product Assessment
                  </Typography>
                  <Typography variant="body2">
                    {summary.product_assessment}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Market Assessment */}
          {summary.market_assessment && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BusinessIcon color="primary" sx={{ mr: 1 }} />
                    Market Assessment
                  </Typography>
                  <Typography variant="body2">
                    {summary.market_assessment}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Financial Assessment */}
          {summary.financial_assessment && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                    Financial Assessment
                  </Typography>
                  <Typography variant="body2">
                    {summary.financial_assessment}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Recommendation */}
      {summary.recommendation && (
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            bgcolor: 'primary.light', 
            color: 'primary.contrastText',
            borderLeft: '4px solid',
            borderColor: 'primary.dark'
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <RecommendIcon sx={{ mr: 1 }} />
            Recommendation
          </Typography>
          <Typography variant="body1">
            {summary.recommendation}
          </Typography>
        </Paper>
      )}

      {/* Snackbar for feedback */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <SnackbarAlert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </SnackbarAlert>
      </Snackbar>
    </Box>
  );
};

export default InvestorSummaryDetail;
